"use client";

import Link from "next/link";
import React, { useState, useLayoutEffect, Suspense } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { CartDrawer } from "../CartDrawer";
import { NavItem } from "./NavItem";
import { UserButton } from "./UserMenu";
import { SearchBar } from "./SearchBar";
import { MobileMenu } from "./MobileMenu";
import { SiteConfigStoryblok } from "@/types/sb-component-types";
import { NavigationMenu, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Container } from "@/components/layout/container";
import { LocaleSwitch } from "@/components/LocaleSwitch";
import { useCurrentLocale } from "@/locales/client";
interface INavBarProps {
  blok: SiteConfigStoryblok;
}
export const MainMenu = ({
  blok
}: INavBarProps) => {
  const [isTransparent, setIsTransparent] = useState(true);
  const pathname = usePathname();
  const locale = useCurrentLocale();
  useLayoutEffect(() => {
    const pageHeader = document.getElementById("page-header");
    const handleScroll = () => {
      if (pageHeader || pathname === `/${locale}`) {
        const currentScrollPosition = window.scrollY;
        const scrollThreshold = 10;
        setIsTransparent(currentScrollPosition < scrollThreshold);
      } else {
        setIsTransparent(false);
      }
    };

    // Initial check
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname, locale]);
  if (pathname.includes("/checkout")) {
    return null;
  }
  return <header className={`fixed z-50 w-full transition-all duration-200 ${isTransparent ? "bg-transparent text-white" : "bg-background/30 text-foreground shadow-md backdrop-blur-xl"}`} data-sentry-component="MainMenu" data-sentry-source-file="index.tsx">
      <Container className="flex h-16 items-center justify-between lg:h-20" data-sentry-element="Container" data-sentry-source-file="index.tsx">
        <div className="flex h-full items-center">
          <Link className="flex h-full items-center" href="/" data-sentry-element="Link" data-sentry-source-file="index.tsx">
            <div className="relative h-12 sm:h-16 lg:h-20">
              <Image priority alt={blok.header_logo.alt ?? ""} className="h-full w-auto object-contain" height={80} src={isTransparent ? blok.header_logo_white?.filename : blok.header_logo?.filename} width={200} data-sentry-element="Image" data-sentry-source-file="index.tsx" />
              <span className="sr-only">Zero Gravity - Kite Shop</span>
            </div>
          </Link>
          <NavigationMenu className="hidden lg:flex" data-sentry-element="NavigationMenu" data-sentry-source-file="index.tsx">
            <NavigationMenuList data-sentry-element="NavigationMenuList" data-sentry-source-file="index.tsx">
              {blok.header_nav?.map((navItem, index) => <NavItem key={index} isTransparent={isTransparent} navItem={navItem} />)}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="flex gap-5">
          <div className="hidden gap-5 lg:flex">
            <SearchBar data-sentry-element="SearchBar" data-sentry-source-file="index.tsx" />
          </div>
          <Suspense fallback={null} data-sentry-element="Suspense" data-sentry-source-file="index.tsx">
            <CartDrawer data-sentry-element="CartDrawer" data-sentry-source-file="index.tsx" />
          </Suspense>

          <UserButton data-sentry-element="UserButton" data-sentry-source-file="index.tsx" />
          <div className="hidden gap-5 lg:flex">
            <LocaleSwitch isTransparent={isTransparent} data-sentry-element="LocaleSwitch" data-sentry-source-file="index.tsx" />
          </div>
          <div className="lg:hidden">
            <MobileMenu blok={blok} isTransparent={isTransparent} data-sentry-element="MobileMenu" data-sentry-source-file="index.tsx" />
          </div>
        </div>
      </Container>
    </header>;
};